import { DateTime } from "luxon";
import type { WorkCalendarData } from "./calendar";
import { DEFAULT_WEEKDAY_PATTERN, workedDayKeysInPeriod } from "./calendar";
import type { OrgCalendarConfig, Period, PeriodType } from "./periods";
import { periodKeyFor, periodsInRange } from "./periods";

/**
 * Target rule resolution (§2.2): most specific period_type with the latest
 * effective_from covering the period; no rule = untracked (null).
 * A ramp = multiple rules; mid-year changes are first-class.
 *
 * Resolution order for a requested period:
 * 1. Exact-type rule (latest effective_from covering the period).
 * 2. DERIVED from finer-type rules: a quarter target for a metric with monthly
 *    targets = sum (cumulative) or mean (average) of the months' targets —
 *    the honest rollup target. Requires config+aggregation.
 * 3. Coarser fallback day→week→month→quarter→year. A CUMULATIVE coarser target
 *    is pro-rated by worked days (a 22-closings month is 1 per worked day), so a
 *    daily cell is never judged against a whole month; an AVERAGE target applies
 *    as-is. Without opts (no calendar/aggregation) the raw value is returned.
 */

export interface TargetRuleData {
  periodType: PeriodType;
  value: number;
  /** upper bound for direction=between */
  maxValue?: number | null;
  effectiveFrom: Date;
  effectiveTo: Date | null;
}

export type Direction = "gte" | "lte" | "gt" | "lt" | "eq" | "between";

/** Resolve min and max together (max pro-rates exactly like the value). */
export function resolveTargetRange(
  rules: TargetRuleData[],
  period: Period,
  opts?: TargetResolutionOpts,
): { target: number | null; max: number | null } {
  const target = resolveTarget(rules, period, opts);
  const withMax = rules.filter((r) => r.maxValue !== null && r.maxValue !== undefined);
  const max = withMax.length
    ? resolveTarget(
        withMax.map((r) => ({ ...r, value: r.maxValue as number })),
        period,
        opts,
      )
    : null;
  return { target, max };
}

export interface TargetResolutionOpts {
  config: OrgCalendarConfig;
  aggregation: "cumulative" | "average";
  /** the seat's work calendar; defaults to Mon–Fri when omitted */
  calendar?: WorkCalendarData;
}

const ORDER: PeriodType[] = ["day", "week", "month", "quarter", "year"];

const FINER: Record<PeriodType, PeriodType[]> = {
  day: [],
  week: ["day"],
  month: ["day"], // weeks straddle months — never derive month from weeks
  quarter: ["month", "day"],
  year: ["quarter", "month", "week", "day"],
};

/** Rules are dated calendar days (stored as UTC midnight); periods live in org time.
 * With a config, coverage is decided on calendar days — a rule dated Oct 1 never
 * leaks into September because of a timezone offset. The latest rule in force at
 * any point in the period wins (a mid-month change governs that month). */
function exactRule(
  rules: TargetRuleData[],
  type: PeriodType,
  period: Period,
  config?: OrgCalendarConfig,
): number | null {
  const dayKey = (dt: Date) => dt.toISOString().slice(0, 10);
  let covers: (r: TargetRuleData) => boolean;
  if (config) {
    const z = { zone: config.timezone };
    const first = DateTime.fromJSDate(period.startsAt, z).toFormat("yyyy-MM-dd");
    const last = DateTime.fromJSDate(period.endsAt, z).minus({ days: 1 }).toFormat("yyyy-MM-dd");
    covers = (r) =>
      dayKey(r.effectiveFrom) <= last && (r.effectiveTo === null || dayKey(r.effectiveTo) >= first);
  } else {
    covers = (r) =>
      r.effectiveFrom < period.endsAt &&
      (r.effectiveTo === null || r.effectiveTo >= period.startsAt);
  }
  const candidates = rules
    .filter((r) => r.periodType === type && covers(r))
    .sort((a, b) => b.effectiveFrom.getTime() - a.effectiveFrom.getTime());
  return candidates.length > 0 ? candidates[0]!.value : null;
}

/** Sub-periods of `type` belonging to `period` (start containment; weeks→year by label). */
function subPeriodsOf(config: OrgCalendarConfig, period: Period, type: PeriodType): Period[] {
  return periodsInRange(config, type, period.startsAt, period.endsAt).filter((s) => {
    if (type === "week" && period.periodType === "year")
      return s.periodKey.slice(0, 4) === period.periodKey;
    const key = periodKeyFor(config, period.periodType, s.startsAt);
    return key === period.periodKey;
  });
}

export function resolveTarget(
  rules: TargetRuleData[],
  period: Period,
  opts?: TargetResolutionOpts,
): number | null {
  // 1. exact
  const exact = exactRule(rules, period.periodType, period, opts?.config);
  if (exact !== null) return exact;

  // 2. derive from finer rules
  if (opts) {
    for (const finer of FINER[period.periodType]) {
      if (!rules.some((r) => r.periodType === finer)) continue;
      const subs = subPeriodsOf(opts.config, period, finer);
      const vals = subs
        .map((s) => exactRule(rules, finer, s, opts.config))
        .filter((v): v is number => v !== null);
      if (vals.length === 0) continue;
      const sum = vals.reduce((a, b) => a + b, 0);
      return opts.aggregation === "cumulative" ? sum : sum / vals.length;
    }
  }

  // 3. coarser fallback (pro-rated for cumulative metrics)
  const start = ORDER.indexOf(period.periodType);
  for (let i = start + 1; i < ORDER.length; i++) {
    const coarser = ORDER[i]!;
    if (!rules.some((r) => r.periodType === coarser)) continue;
    const plain = exactRule(rules, coarser, period, opts?.config);
    if (!opts || opts.aggregation !== "cumulative") {
      if (plain !== null) return plain;
      continue;
    }
    // a week can straddle two months: take each overlapping coarse period's rule
    // and its worked-day share of this period; sum the shares
    const cal = opts.calendar ?? { weekdayPattern: DEFAULT_WEEKDAY_PATTERN, overrides: {} };
    const mine = new Set(workedDayKeysInPeriod(cal, opts.config, period));
    const coarse = periodsInRange(opts.config, coarser, period.startsAt, period.endsAt);
    let sum = 0;
    let found = false;
    for (const c of coarse) {
      const v = exactRule(rules, coarser, c, opts.config);
      if (v === null) continue;
      found = true;
      const cDays = workedDayKeysInPeriod(cal, opts.config, c);
      if (cDays.length === 0) continue;
      const overlap = cDays.filter((d) => mine.has(d)).length;
      sum += (v * overlap) / cDays.length;
    }
    if (found) return sum;
  }
  return null;
}

export function compareToTarget(
  direction: Direction,
  actual: number,
  target: number,
  max: number | null = null,
): boolean {
  switch (direction) {
    case "between":
      return actual >= target && (max === null || actual <= max);
    case "gte":
      return actual >= target;
    case "lte":
      return actual <= target;
    case "gt":
      return actual > target;
    case "lt":
      return actual < target;
    case "eq":
      return actual === target;
  }
}

/** Direction-aware attainment for the goal score (§6). For lte/lt metrics,
 * attainment = target/actual (spending less than target overachieves). */
export function attainment(
  direction: Direction,
  actual: number,
  target: number,
  cap: number,
  max: number | null = null,
): number {
  let raw: number;
  switch (direction) {
    case "between": {
      // inside the range = 100%; outside, fall off by distance relative to the range width
      const hi = max ?? target;
      if (actual >= target && actual <= hi) {
        raw = 1;
        break;
      }
      const width = Math.max(hi - target, Math.abs(target) || 1);
      const dist = actual < target ? target - actual : actual - hi;
      raw = Math.max(0, 1 - dist / width);
      break;
    }
    case "gte":
    case "gt":
      raw = target === 0 ? (actual >= 0 ? 1 : 0) : actual / target;
      break;
    case "lte":
    case "lt":
      if (actual === 0) raw = target === 0 ? 1 : cap;
      else raw = target / actual;
      break;
    case "eq":
      raw = actual === target ? 1 : 0;
      break;
  }
  if (raw < 0) raw = 0;
  return Math.min(raw, cap);
}

/** Human target: integers stay integers, pro-rated values get ≤2 decimals ("0.77", "8.6"). */
export function formatTarget(v: number | null): string {
  if (v === null) return "—";
  if (Number.isInteger(v)) return String(v);
  const r = Math.abs(v) >= 10 ? v.toFixed(1) : v.toFixed(2);
  return r.replace(/\.?0+$/, "");
}
